import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { Check, Clock, Download, Filter, RefreshCw, Search, ShieldCheck, ThumbsDown, Users, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import PendingMemberPayments from "@/pages/admin/PendingMemberPayments";
import {
  EmptyRows,
  PortalActionButton,
  PortalBadge,
  PortalPageHeader,
  PortalPanel,
  PortalStatCard,
  SummaryLine,
} from "@/components/admin/PortalPrimitives";
import { cn, formatCurrency } from "@/lib/utils";

const requestTone = (type: string) => {
  const value = type.toLowerCase();
  if (value.includes("loan")) return "amber" as const;
  if (value.includes("payment") || value.includes("repayment")) return "purple" as const;
  if (value.includes("member")) return "blue" as const;
  return "green" as const;
};

export default function ApprovalsCenter() {
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all");
  const [showPaymentWorkflow, setShowPaymentWorkflow] = useState(false);

  const approvalsQuery = useQuery(
    ["approvals-center-report"],
    async () => {
      const response = await api.get("/reports/approvals");
      return response.data?.data;
    },
    { staleTime: 15000, refetchInterval: 30000 }
  );

  const data = approvalsQuery.data || {};
  const summary = data.summary || {};
  const pendingItems = data.pendingItems || [];

  const groupedCounts = useMemo(() => {
    return pendingItems.reduce((acc: Record<string, number>, item: any) => {
      const type = String(item.type || "other");
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
  }, [pendingItems]);

  const filteredItems = pendingItems.filter((item: any) => {
    const query = search.trim().toLowerCase();
    const type = String(item.type || "other").toLowerCase();
    const matchesSearch = !query || [item.id, item.type, item.description, item.submittedBy].some((value) => String(value || "").toLowerCase().includes(query));
    const matchesTab = tab === "all" || type.includes(tab);
    return matchesSearch && matchesTab;
  });

  const loading = approvalsQuery.isLoading;

  if (showPaymentWorkflow) {
    return (
      <div className="space-y-5 py-4">
        <PortalPageHeader
          title="Approvals Center"
          description="Review member payment submissions using the existing multi-approval workflow."
          actions={<PortalActionButton variant="outline" onClick={() => setShowPaymentWorkflow(false)}>Back to Overview</PortalActionButton>}
        />
        <PendingMemberPayments />
      </div>
    );
  }

  return (
    <div className="space-y-5 py-4">
      <PortalPageHeader
        title="Approvals Center"
        description="Review and approve or reject requests that require authorization."
        actions={
          <>
            <PortalActionButton variant="outline" onClick={() => approvalsQuery.refetch()}>
              <RefreshCw className={cn("mr-2 h-4 w-4", approvalsQuery.isFetching && "animate-spin")} /> Refresh
            </PortalActionButton>
            <PortalActionButton variant="outline"><Download className="mr-2 h-4 w-4" /> Export</PortalActionButton>
            <PortalActionButton onClick={() => setShowPaymentWorkflow(true)}><ShieldCheck className="mr-2 h-4 w-4" /> Open Payment Workflow</PortalActionButton>
          </>
        }
      />

      <div className="grid gap-5 xl:grid-cols-[1fr_290px]">
        <div className="space-y-5">
          <div className="grid gap-4 md:grid-cols-5">
            <PortalStatCard title="Total Pending" value={summary.pendingApprovals || pendingItems.length} subtitle="Requests waiting for approval" icon={<Clock className="h-5 w-5" />} tone="green" loading={loading} />
            <PortalStatCard title="Loan Requests" value={groupedCounts.loan || 0} subtitle="Loan applications pending" icon={<ShieldCheck className="h-5 w-5" />} tone="amber" loading={loading} />
            <PortalStatCard title="Payment Requests" value={groupedCounts.payment || 0} subtitle="Payment related requests" icon={<Check className="h-5 w-5" />} tone="purple" loading={loading} />
            <PortalStatCard title="Member Requests" value={groupedCounts.member || 0} subtitle="Member related requests" icon={<Users className="h-5 w-5" />} tone="blue" loading={loading} />
            <PortalStatCard title="Rejected" value={summary.rejected || 0} subtitle="Rejected requests" icon={<X className="h-5 w-5" />} tone="red" loading={loading} />
          </div>

          <div className="flex flex-wrap gap-6 border-b border-slate-200 text-sm font-semibold">
            {[
              ["all", "All Requests"],
              ["loan", "Loan Requests"],
              ["payment", "Payment Adjustments"],
              ["member", "Member Requests"],
              ["other", "Other Requests"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={cn("border-b-2 px-1 pb-3", tab === key ? "border-[#009A44] text-[#009A44]" : "border-transparent text-slate-500")}
              >
                {label}
              </button>
            ))}
          </div>

          <PortalPanel title="Filters">
            <div className="grid gap-3 lg:grid-cols-[1fr_180px_180px_180px_auto]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input value={search} onChange={(event) => setSearch(event.target.value)} className="pl-9" placeholder="Search by member name or request ID..." />
              </div>
              <select className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"><option>All Types</option></select>
              <select className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"><option>Pending</option></select>
              <select className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"><option>All Users</option></select>
              <Button variant="outline"><Filter className="mr-2 h-4 w-4" /> Reset</Button>
            </div>
          </PortalPanel>

          <PortalPanel title="Pending Requests">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-slate-500">
                    <th className="px-3 py-3 font-semibold">Request ID</th>
                    <th className="px-3 py-3 font-semibold">Type</th>
                    <th className="px-3 py-3 font-semibold">Member</th>
                    <th className="px-3 py-3 font-semibold">Request Details</th>
                    <th className="px-3 py-3 text-right font-semibold">Amount</th>
                    <th className="px-3 py-3 font-semibold">Requested On</th>
                    <th className="px-3 py-3 font-semibold">Status</th>
                    <th className="px-3 py-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    Array.from({ length: 8 }).map((_, index) => (
                      <tr key={index}><td className="px-3 py-4" colSpan={8}><Skeleton className="h-5 w-full" /></td></tr>
                    ))
                  ) : filteredItems.length === 0 ? (
                    <EmptyRows colSpan={8} message="No pending requests found." />
                  ) : (
                    filteredItems.map((item: any) => (
                      <tr key={item.id} className="hover:bg-slate-50/70">
                        <td className="whitespace-nowrap px-3 py-3 font-mono text-xs font-semibold text-slate-800">{item.id}</td>
                        <td className="px-3 py-3"><PortalBadge tone={requestTone(String(item.type || ""))}>{item.type || "Request"}</PortalBadge></td>
                        <td className="px-3 py-3 font-semibold text-slate-900">{item.submittedBy || "-"}</td>
                        <td className="max-w-[340px] px-3 py-3 text-slate-700">{item.description || "-"}</td>
                        <td className="px-3 py-3 text-right font-semibold">{String(item.description || "").match(/([\d,]+)\s*FRW/)?.[1] ? `${String(item.description || "").match(/([\d,]+)\s*FRW/)?.[1]} FRW` : "-"}</td>
                        <td className="whitespace-nowrap px-3 py-3 text-slate-600">{item.submittedAt ? new Date(item.submittedAt).toLocaleString() : "-"}</td>
                        <td className="px-3 py-3"><PortalBadge tone="amber">Pending</PortalBadge></td>
                        <td className="px-3 py-3">
                          <div className="flex justify-end gap-2">
                            <Button size="sm" className="bg-[#009A44] hover:bg-[#00843a]" onClick={() => setShowPaymentWorkflow(String(item.type || "").toLowerCase() === "payment")}>Approve</Button>
                            <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50"><ThumbsDown className="mr-1 h-3 w-3" /> Reject</Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </PortalPanel>
        </div>

        <div className="space-y-5">
          <PortalPanel title="Approval Summary">
            <div className="divide-y divide-slate-100">
              <SummaryLine label="Total Requests" value={pendingItems.length} />
              <SummaryLine label="Pending" value={summary.pendingApprovals || pendingItems.length} tone="amber" />
              <SummaryLine label="Rejected" value={summary.rejected || 0} tone="red" />
              <SummaryLine label="Approved by President" value={summary.approvedByPresident || 0} tone="green" />
              <SummaryLine label="Approved by Secretary" value={summary.approvedBySecretary || 0} tone="green" />
              <SummaryLine label="Approved by Accountant" value={summary.approvedByAccountant || 0} tone="green" />
            </div>
          </PortalPanel>
          <PortalPanel title="Requests by Type">
            <div className="space-y-2">
              {Object.entries(groupedCounts).length === 0 ? (
                <p className="text-sm text-slate-500">No pending request categories.</p>
              ) : (
                Object.entries(groupedCounts).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2">
                    <PortalBadge tone={requestTone(type)}>{type}</PortalBadge>
                    <span className="font-bold text-slate-900">{String(count)}</span>
                  </div>
                ))
              )}
            </div>
          </PortalPanel>
          <PortalPanel title="Quick Actions">
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start" onClick={() => setShowPaymentWorkflow(true)}><Check className="mr-2 h-4 w-4 text-[#009A44]" /> Payment Approval Workflow</Button>
              <Button variant="outline" className="w-full justify-start"><ThumbsDown className="mr-2 h-4 w-4 text-red-600" /> Bulk Reject</Button>
              <Button variant="outline" className="w-full justify-start"><Clock className="mr-2 h-4 w-4 text-[#009A44]" /> Approval History</Button>
            </div>
          </PortalPanel>
        </div>
      </div>
    </div>
  );
}
